/**
 * The two role pickers, shared by the member settings dialog and onboarding.
 *
 * Both are fully controlled: they hold no selection state and call no API, so
 * onboarding can render them against a person who does not exist yet while the
 * member dialog renders them against one who does. Extracted rather than
 * copied so "Agency Roles / Clinic Roles" looks and behaves identically in
 * both places -- the same operators use both screens.
 */

import React, { useMemo } from 'react';
import { Badge, Checkbox } from '@roger/ui-components';
import { Building2, Check } from 'lucide-react';
import type { RoleResponse } from '../../../services/api/types.gen';
import type { AgencyClinicResponse } from '../../clinician-manager/types';
import { isAssignableClinicianRole } from '../../../utils/rbacRoles';
import {
  ClinicDisciplinesSection,
  type ClinicDisciplinesControl,
} from '../../clinician-manager/ClinicDisciplinesSection';

// ─── AgencyRoleCard ──────────────────────────────────────────────────────────

export type AgencyRoleCardProps = {
  role: RoleResponse;
  selected: boolean;
  onToggle: (roleId: string) => void;
};

export const AgencyRoleCard: React.FC<AgencyRoleCardProps> = React.memo(
  function AgencyRoleCard({ role, selected, onToggle }) {
    return (
      <button
        type='button'
        onClick={() => onToggle(role.id)}
        aria-pressed={selected}
        title={role.description ?? undefined}
        className={[
          'flex min-w-0 items-start gap-2 rounded-md border px-3 py-2 text-left transition-colors',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1',
          selected
            ? 'border-primary bg-primary/5'
            : 'border-border bg-card hover:border-primary/40 hover:bg-muted/40',
        ].join(' ')}
      >
        <span
          className={[
            'mt-0.5 flex h-4 w-4 flex-shrink-0 items-center justify-center rounded border',
            selected
              ? 'border-primary bg-primary text-primary-foreground'
              : 'border-input',
          ].join(' ')}
        >
          {selected && <Check className='h-3 w-3' />}
        </span>
        <span className='min-w-0'>
          <span className='block text-sm font-medium leading-snug'>
            {role.name}
          </span>
          {role.description && (
            <span className='block truncate text-xs text-muted-foreground'>
              {role.description}
            </span>
          )}
        </span>
      </button>
    );
  }
);

// ─── ClinicRoleSection ────────────────────────────────────────────────────────

export type ClinicRoleSectionProps = {
  role: RoleResponse;
  clinics: AgencyClinicResponse[];
  selectedClinicIds: Set<string>;
  onToggleClinic: (roleId: string, clinicId: string) => void;
  onSelectAll: (roleId: string, allClinicIds: string[]) => void;
  onClear: (roleId: string) => void;
  clinicDisciplines?: ClinicDisciplinesControl;
};

export const ClinicRoleSection: React.FC<ClinicRoleSectionProps> = React.memo(
  function ClinicRoleSection({
    role,
    clinics,
    selectedClinicIds,
    onToggleClinic,
    onSelectAll,
    onClear,
    clinicDisciplines,
  }) {
    const allSelected =
      clinics.length > 0 && selectedClinicIds.size === clinics.length;
    const someSelected = selectedClinicIds.size > 0;
    const allClinicIds = useMemo(() => clinics.map(c => c.id), [clinics]);
    const showDisciplines =
      !!clinicDisciplines && isAssignableClinicianRole(role) && someSelected;

    return (
      <div
        className={[
          'rounded-md border transition-colors',
          someSelected
            ? 'border-primary/40 bg-primary/3'
            : 'border-border bg-card',
        ].join(' ')}
      >
        {/* Role header */}
        <div className='flex items-start justify-between gap-3 px-3 py-2'>
          <div className='flex items-start gap-2.5 min-w-0'>
            <Building2
              className={`mt-0.5 h-4 w-4 flex-shrink-0 ${someSelected ? 'text-primary' : 'text-muted-foreground'}`}
            />
            <div className='min-w-0'>
              <div className='flex items-center gap-2 flex-wrap'>
                <span className='font-semibold text-sm'>{role.name}</span>
                {someSelected && (
                  <Badge variant='default' className='text-xs'>
                    {selectedClinicIds.size}{' '}
                    {selectedClinicIds.size === 1 ? 'clinic' : 'clinics'}
                  </Badge>
                )}
              </div>
              {role.description && (
                <p className='mt-0.5 text-xs text-muted-foreground leading-relaxed'>
                  {role.description}
                </p>
              )}
            </div>
          </div>

          <div className='flex items-center gap-1.5 flex-shrink-0'>
            <button
              type='button'
              onClick={() =>
                allSelected
                  ? onClear(role.id)
                  : onSelectAll(role.id, allClinicIds)
              }
              className='text-xs text-primary hover:underline focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring rounded px-1'
            >
              {allSelected ? 'Clear all' : 'Select all'}
            </button>
          </div>
        </div>

        {/* Clinic grid */}
        {clinics.length === 0 ? (
          <p className='px-3 pb-3 text-xs text-muted-foreground'>
            No clinics available.
          </p>
        ) : (
          <div className='grid grid-cols-2 gap-x-4 gap-y-1 px-3 pb-3 sm:grid-cols-3'>
            {clinics.map(clinic => (
              <label
                key={clinic.id}
                className='flex cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 text-sm transition-colors hover:bg-muted/60'
              >
                <Checkbox
                  checked={selectedClinicIds.has(clinic.id)}
                  onCheckedChange={() => onToggleClinic(role.id, clinic.id)}
                />
                <span className='truncate text-xs font-medium'>
                  {clinic.name}
                </span>
              </label>
            ))}
          </div>
        )}

        {showDisciplines && (
          <div className='border-t px-3 pb-3 pt-3'>
            <ClinicDisciplinesSection embedded {...clinicDisciplines} />
          </div>
        )}
      </div>
    );
  }
);
